import { useEffect, useState } from 'react';
import { Flame, Crown, Zap } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2K PR 2 — Rivalry Badge Strip (a horizontal scroller of badge cards).
 *
 * Two modes:
 *   mode="city"     → /arenax/rivalries/badges?city=&sport=
 *   mode="season"   → /arenax/seasons/{seasonId}/rivalry-badges
 *
 * Tone: each card is a lore artifact — never an analytics tile.
 * See narrative_style_guide.md.
 */
export default function ArenaxRivalryBadgeStrip({
  mode = 'city',
  city,
  sport,
  seasonId,
  title = 'Rivalries to watch',
  testId = 'arenax-rivalry-badges',
}) {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url;
    if (mode === 'season' && seasonId) {
      url = `/arenax/seasons/${seasonId}/rivalry-badges`;
    } else {
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (sport) params.set('sport', sport);
      url = `/arenax/rivalries/badges${params.toString() ? '?' + params.toString() : ''}`;
    }
    api.get(url)
      .then(r => setBadges(r.data?.badges || []))
      .catch(() => setBadges([]))
      .finally(() => setLoading(false));
  }, [mode, city, sport, seasonId]);

  if (loading || badges.length === 0) return null;

  return (
    <div className="rounded-2xl border border-rose-500/15 p-5"
      data-testid={testId}
      style={{ background: 'linear-gradient(160deg, rgba(244,63,94,0.05), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="h-4 w-4 text-rose-300" />
          <span className="text-[10px] tracking-[0.3em] text-rose-300 font-bold uppercase">
            {title}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
        {badges.slice(0, 4).map(b => (
          <BadgeCard key={`${b.kind}-${b.squad_a_id}-${b.squad_b_id}`} badge={b} />
        ))}
      </div>
    </div>
  );
}

const KIND_STYLE = {
  most_heated: {
    border: 'border-rose-500/35',
    bg: 'linear-gradient(135deg, rgba(244,63,94,0.10), var(--ax-card-bg-soft))',
    accent: 'text-rose-200',
    chipBg: 'bg-rose-500/15 border-rose-500/30 text-rose-200',
  },
  closest: {
    border: 'border-cyan-500/35',
    bg: 'linear-gradient(135deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))',
    accent: 'text-cyan-200',
    chipBg: 'bg-cyan-500/15 border-cyan-500/30 text-cyan-200',
  },
  championship: {
    border: 'border-amber-500/40',
    bg: 'linear-gradient(135deg, rgba(245,158,11,0.10), var(--ax-card-bg-soft))',
    accent: 'text-amber-200',
    chipBg: 'bg-amber-500/15 border-amber-500/30 text-amber-200',
  },
  emerging: {
    border: 'border-violet-500/35',
    bg: 'linear-gradient(135deg, rgba(139,92,246,0.10), var(--ax-card-bg-soft))',
    accent: 'text-violet-200',
    chipBg: 'bg-violet-500/15 border-violet-500/30 text-violet-200',
  },
};

function BadgeCard({ badge }) {
  const style = KIND_STYLE[badge.kind] || KIND_STYLE.most_heated;
  return (
    <div className={`rounded-xl border ${style.border} p-4 transition-all hover:translate-y-[-1px]`}
      data-testid={`arenax-rivalry-badge-${badge.kind}-${badge.squad_a_id}-${badge.squad_b_id}`}
      style={{ background: style.bg }}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${style.chipBg}`}>
          <span className="text-sm leading-none">{badge.emoji}</span>
          {badge.label}
        </span>
      </div>
      <h4 className="text-base sm:text-lg font-black text-slate-100 leading-tight mb-1"
        data-testid={`arenax-rivalry-badge-headline-${badge.kind}`}>
        {badge.headline}
      </h4>
      <p className={`text-xs ${style.accent} italic`}>
        {badge.subtitle}
      </p>
    </div>
  );
}
